import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api, authHeaders, describeError } from "../lib/api";
import type { DocumentRequest } from "../lib/api";

/** เหตุผลสั้นสุดที่ยอมให้ส่ง — เหตุผลนี้ขึ้นในประวัติคำร้อง จึงไม่ให้ใส่แค่ตัวอักษรเดียว */
const MIN_REASON = 5;

/**
 * ข้ามขั้นนายบ้านก่อนกำหนด (ADR-0031 §4) — `POST /v1/admin/requests/{id}/village-certification/waive`
 *
 * ใช้ร่วมกันทั้งคิวและหน้ารายละเอียด; ผู้เรียกต้องเช็ค APP-06 เอง (server เช็คซ้ำ + scope)
 * ข้ามแล้วคำร้องเข้าคิวตาแสงทันที ใบที่ออกจะไม่มีลายเซ็นนายบ้าน
 */
export function VillageWaiveModal({
  requestId,
  villageName,
  onClose,
}: {
  requestId: string;
  villageName: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const trimmed = reason.trim();

  const waive = useMutation<DocumentRequest, Error, void>({
    mutationFn: async () => {
      const { data, error, response } = await api.POST("/v1/admin/requests/{id}/village-certification/waive", {
        params: { path: { id: requestId } },
        body: { note: trimmed },
        headers: authHeaders(),
      });
      if (error || !data) {
        if (response.status === 422) throw new Error("ຄຳຮ້ອງນີ້ບໍ່ໄດ້ລໍນາຍບ້ານແລ້ວ — ໂຫຼດໜ້າໃໝ່ເພື່ອເບິ່ງສະຖານະລ່າສຸດ");
        throw new Error(describeError(error, response.status));
      }
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["requests"] });
      onClose();
    },
  });

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ຂ້າມຂັ້ນນາຍບ້ານ">
      <form
        className="modal modal--sm"
        onSubmit={(e) => {
          e.preventDefault();
          if (trimmed.length >= MIN_REASON) waive.mutate();
        }}
      >
        <div className="modal__header">
          <h2 className="modal__title">ຂ້າມຂັ້ນນາຍບ້ານ?</h2>
          <p className="caption">
            {villageName ? `ນາຍບ້ານ${villageName}` : "ນາຍບ້ານ"} ຍັງບໍ່ໄດ້ຢັ້ງຢືນ — ຂ້າມແລ້ວຄຳຮ້ອງເຂົ້າຄິວຕາແສງທັນທີ ແລະ
            ເອກະສານທີ່ອອກຈະບໍ່ມີລາຍເຊັນນາຍບ້ານ · ເຫດຜົນຈະຂຶ້ນໃນປະຫວັດຄຳຮ້ອງ
          </p>
        </div>
        {waive.isError ? <div className="alert-error">{waive.error.message}</div> : null}
        <div className="stack" style={{ marginBottom: "var(--space-6)" }}>
          <div>
            <label className="form-field-label" htmlFor="village-waive-reason">
              ເຫດຜົນ <span className="required">*</span>
            </label>
            <textarea
              id="village-waive-reason"
              className="input-field"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="ເຊັ່ນ ນາຍບ້ານບໍ່ຢູ່ ຕິດຕໍ່ບໍ່ໄດ້"
              autoFocus
            />
            {trimmed.length > 0 && trimmed.length < MIN_REASON ? (
              <p className="caption">ກະລຸນາຂຽນເຫດຜົນຢ່າງໜ້ອຍ {MIN_REASON} ຕົວອັກສອນ</p>
            ) : null}
          </div>
        </div>
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose}>
            ຍົກເລີກ
          </button>
          <button type="submit" className="btn-danger" disabled={trimmed.length < MIN_REASON || waive.isPending}>
            {waive.isPending ? "ກຳລັງຂ້າມ…" : "ຢືນຢັນຂ້າມຂັ້ນ"}
          </button>
        </div>
      </form>
    </div>
  );
}
